import Vue from 'vue';
import NotificationApi, { CRMNotificationObject } from '@/modules/notification/NotificationApi';
import Logger from '@/services/log/Logger';
import AuthService from '@/modules/user/AuthService';
import CRMNotification from '@/models/notification/CRMNotification';

class NotificationService {
  private notifications: CRMNotificationObject = {
    items: [],
    totalUnread: 0,
  };

  private socket: Nullable<WebSocket> = null;
  private reconnectTimeoutId: Nullable<ReturnType<typeof setTimeout>> = null;

  public get Observable() {
    return Vue.observable(this.notifications);
  }

  public async loadMore(page: number) {
    const response = await NotificationApi.getNotifications(page);
    if (response.items && response.items.length > 0) {
      response.items.forEach(item => this.addNotificationIfMissing(item));
      this.notifications.totalUnread = response.totalUnread;
    }
    return response;
  }

  public async loadNotification() {
    const response = await NotificationApi.getNotifications();
    const uniqueItems = response.items.filter(
      (item, index, allItems) => allItems.findIndex(existingItem => existingItem.id === item.id) === index
    );
    this.notifications.items = uniqueItems;
    this.notifications.totalUnread = response.totalUnread;
    return response;
  }

  private isNotificationInList(notificationItem: CRMNotification): boolean {
    return this.notifications.items.some(existingItem => existingItem.id === notificationItem.id);
  }

  public async loadMissed() {
    const response = await NotificationApi.getNotifications();
    response.items.forEach((item) => {
      if (!item.isRead && !this.isNotificationInList(item)) {
        this.notifications.items.unshift(item);
        this.notifications.totalUnread++;
      }
    });
  }

  public disconnect() {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.socket?.close();
    this.socket = null;
  }

  public connect() {
    if (this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
      return;
    }
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    this.socket = NotificationApi.socket;
    if (this.socket) {
      this.socket.onmessage = (data) => this.handleNotification(data);
      this.socket.onerror = (error: Event) => {
        Logger.warning('Occurred websocket error', {error: error, });
      };
      this.socket.onclose = () => {
        this.socket = null;
        if (AuthService.isLoggedIn) {
          this.reconnectTimeoutId = setTimeout(() => {
            this.connect();
            this.loadMissed();
          }, 5000);
        }
      };
    }
  }

  private handleNotification(data: MessageEvent) {
    const msgData: any = JSON.parse(data.data) as any;
    if (msgData.totalUnread !== undefined) {
      this.notifications.totalUnread = msgData.totalUnread;
    }
    if (msgData.data) {
      const wasAdded = this.addNotificationIfMissing(msgData, true);
      if (wasAdded && msgData.totalUnread === undefined) {
        this.notifications.totalUnread++;
      }
    }
  }

  private addNotificationIfMissing(notification: CRMNotification, prepend: boolean = false): boolean {
    if (this.isNotificationInList(notification)) {
      return false;
    }
    if (prepend) {
      this.notifications.items.unshift(notification);
    } else {
      this.notifications.items.push(notification);
    }
    return true;
  }
}

export default new NotificationService();
